/***************************************************************************
 * EditableRow — custom DataGrid row component installed via the
 * DataGridPro `components.Row` slot.
 *
 * When the row is the currently-editing row it wraps the default GridRow
 * in a Formik tree so every cell renderer inside the row can call
 * useFormikContext().  All other rows fall through to GridRow unchanged.
 *
 * The pencil / check / cancel overlay is rendered separately by
 * HoverRowActionsOverlay at the grid level — not by this component.
 ***************************************************************************/

import React, {useContext, useEffect, useState} from "react";
import {GridRow, GridRowProps} from "@mui/x-data-grid-pro";
import {Form, Formik} from "formik";
import * as Yup from "yup";
import {FormikValueTracker} from "qqq/components/forms/formAdjustersAndRules";
import {RowBannerState, RowEditContext} from "./RowEditContext";
import {EDIT_CELL_HORIZONTAL_PADDING_PX, ROW_BANNER_RESERVED_HEIGHT_PX} from "./rowEditConstants";

interface ExternalProps
{
   initialValues: { [k: string]: any };
   validationSchema: Yup.ObjectSchema<any>;
   onSubmit: (values: any, helpers: any) => Promise<void>;
   onFieldChange: (fn: string, ov: any, nv: any, changes: { [k: string]: any }) => void;
   formValuesRef: React.MutableRefObject<{ [k: string]: any }>;
   formValuesJSONRef: React.MutableRefObject<string>;
   setFieldValueRef: React.MutableRefObject<((name: string, value: any, shouldValidate?: boolean) => void) | null>;
   formikSubmitRef: React.MutableRefObject<(() => void) | null>;
   /** Set by the host so EditableRow can push validation-error state to the banner. */
   setRowBannerState: React.Dispatch<React.SetStateAction<RowBannerState>>;
}

export function makeEditableRow(external: ExternalProps)
{
   return function EditableRow(props: GridRowProps): JSX.Element
   {
      ////////////////////////////////////////////////////////////////////////////////////
      // Read the edit-row context from the OUTER Provider (in RecordQuery /            //
      // RecordGridWidget) rather than the external closure.  This keeps EditableRow's  //
      // component identity stable across rowBannerState changes — Formik stays mounted //
      // and its touched/errors state survives a failed save-click validation pass.     //
      ////////////////////////////////////////////////////////////////////////////////////
      const currentCtx = useContext(RowEditContext);

      //////////////////////////////////////////////////////////////////////////////////
      // Two independent signals:                                                     //
      //   - bannerShowsContent: whether RowBanner renders a row-level Alert.  Drives //
      //     the spacer height.  hasFieldErrors/saveSuccess don't render one.         //
      //   - rowIsExpanded: whether the row needs to grow (via getRowHeight="auto")   //
      //     and apply top-aligning CSS.  ALL non-idle kinds expand the row — even    //
      //     hasFieldErrors, since inline per-cell errors push cells taller.          //
      //                                                                              //
      // Computed BEFORE any conditional returns below so the hooks that follow have  //
      // stable call order (Rules of Hooks).                                          //
      //////////////////////////////////////////////////////////////////////////////////
      const bannerKind = currentCtx?.rowBannerState.kind ?? "none";
      const bannerShowsContent = bannerKind === "formDisabled" || bannerKind === "saveError";
      const rowIsExpanded = bannerKind !== "none" && bannerKind !== "saveSuccess";

      ////////////////////////////////////////////////////////////////////////////////
      // Track the portaled banner's rendered height so the spacer can grow when    //
      // the message wraps to multiple lines.  The banner lives in a portal at grid //
      // root (EditingRowBannerOverlay), so we find it via DOM query and observe.   //
      // Always-called hook — placed BEFORE conditional early returns.              //
      ////////////////////////////////////////////////////////////////////////////////
      const [measuredBannerHeight, setMeasuredBannerHeight] = useState<number>(ROW_BANNER_RESERVED_HEIGHT_PX);
      useEffect(() =>
      {
         if (!bannerShowsContent)
         {
            setMeasuredBannerHeight(ROW_BANNER_RESERVED_HEIGHT_PX);
            return;
         }
         const content = document.querySelector(".qqq-row-edit-banner-overlay .qqq-row-edit-row-error-text") as HTMLElement | null;
         if (!content)
         {
            return;
         }
         const measure = () =>
         {
            const h = content.getBoundingClientRect().height;
            setMeasuredBannerHeight(Math.max(ROW_BANNER_RESERVED_HEIGHT_PX, Math.ceil(h) + 8));
         };
         measure();
         const ro = new ResizeObserver(measure);
         ro.observe(content);
         window.addEventListener("resize", measure);
         return () =>
         {
            ro.disconnect();
            window.removeEventListener("resize", measure);
         };
      }, [bannerShowsContent, bannerKind]);

      const isEditingThisRow = currentCtx?.editingRowId === props.rowId;
      if (!isEditingThisRow)
      {
         return <GridRow {...props} />;
      }

      ///////////////////////////////////////////////////////////////////////////////////////
      // Do not mount the Formik tree until initialValues is populated (non-empty).        //
      // useRowEditor populates initialValues asynchronously in a useEffect; mounting with //
      // {} first and then receiving real values via enableReinitialize causes the entire  //
      // Formik tree (and all input DOM nodes) to unmount-and-remount, making them stale   //
      // in any test or code that held a reference before the reset.                       //
      ///////////////////////////////////////////////////////////////////////////////////////
      if (Object.keys(external.initialValues).length === 0)
      {
         return <GridRow {...props} />;
      }

      const bannerSpacerHeight = bannerShowsContent ? measuredBannerHeight : 0;

      return (
         <Formik
            initialValues={external.initialValues}
            validationSchema={external.validationSchema}
            onSubmit={external.onSubmit}
            enableReinitialize
         >
            {(formik) =>
            {
               /////////////////////////////////////////////////////////////////////////////////
               // Expose a wrapped submit to the host so the floating Save button can:        //
               //   1. run validation (touch all fields so errors display)                    //
               //   2. set a banner message if validation fails (otherwise the click would    //
               //      silently do nothing — Formik's submitForm is a no-op on invalid forms) //
               //   3. submit if valid                                                        //
               /////////////////////////////////////////////////////////////////////////////////
               external.formikSubmitRef.current = async () =>
               {
                  const errors = await formik.validateForm();
                  if (errors && Object.keys(errors).length > 0)
                  {
                     const touched: {[k: string]: boolean} = {};
                     for (const k in errors) touched[k] = true;
                     formik.setTouched(touched, false);
                     external.setRowBannerState({kind: "hasFieldErrors", errorFieldNames: Object.keys(errors)});

                     //////////////////////////////////////////////////////////////////////////////
                     // Scroll the leftmost errored cell into view (so the user can see the red  //
                     // outline + error text) and focus its input.  Cells are queried across all //
                     // three pinned sections; DOM order is left-pinned → main → right-pinned,   //
                     // which matches visual left-to-right order.  rAF lets React commit the     //
                     // error-state render before we measure / scroll.                           //
                     //////////////////////////////////////////////////////////////////////////////
                     requestAnimationFrame(() =>
                     {
                        const errorFieldNames = new Set(Object.keys(errors));
                        const cells = document.querySelectorAll(".qqq-row-edit-editing-row .MuiDataGrid-cell[data-field]");
                        for (const cell of Array.from(cells))
                        {
                           const field = cell.getAttribute("data-field");
                           if (field && errorFieldNames.has(field))
                           {
                              cell.scrollIntoView({inline: "nearest", block: "nearest", behavior: "smooth"});
                              const input = cell.querySelector("input, textarea, [role='combobox']") as HTMLElement | null;
                              if (input)
                              {
                                 input.focus({preventScroll: true});
                              }
                              break;
                           }
                        }
                     });
                     return;
                  }
                  formik.submitForm();
               };
               //////////////////////////////////////////////////////////////////////////////
               // Tag the row "expanded" whenever the banner is showing content — CSS uses //
               // this to top-align cell content so field error text beneath one input     //
               // doesn't push that input higher than the inputs in cells without errors.  //
               //////////////////////////////////////////////////////////////////////////////
               const className = "qqq-row-edit-editing-row compactForm"
                  + (rowIsExpanded ? " qqq-row-edit-expanded" : "");
               return (
                  <Form>
                     <div
                        className={className}
                        style={{"--qqq-edit-cell-padding": `${EDIT_CELL_HORIZONTAL_PADDING_PX}px`} as React.CSSProperties}
                     >
                        <GridRow {...props} />
                        <div
                           className="qqq-row-edit-banner-spacer"
                           aria-hidden="true"
                           style={{height: bannerSpacerHeight}}
                        />
                        <FormikValueTracker
                           formValuesRef={external.formValuesRef}
                           formValuesJSONRef={external.formValuesJSONRef}
                           setFieldValueRef={external.setFieldValueRef}
                           onFieldChange={external.onFieldChange}
                        />
                     </div>
                  </Form>
               );
            }}
         </Formik>
      );
   };
}
